import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";

const RANGE_OPTIONS = [
  { label: "Last 7 days",  value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "All time",     value: 3650 },
];

const HAUL_LABELS = {
  basic: "Basic",
  good: "Good",
  great: "Great",
  giant: "Giant",
  jackpot: "Jackpot",
};

const ECONOMY_LABELS = {
  welcome: "Welcome grant",
  dig: "Daily Dig payout",
  dig_duplicate: "Dig — duplicate refund",
  medal_claim: "Medal claim",
  purchase: "Shop purchase",
  sell: "Shop sell-back",
  invite_reward: "Invite reward",
  adjustment: "Manual adjustment",
};

function StatCard({ label, value, suffix, hint }) {
  return (
    <Box sx={{
      flex: 1,
      minWidth: 180,
      p: 3,
      borderRadius: 3,
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        {value ?? "—"}
        {suffix && (
          <Typography component="span" variant="h6" sx={{ ml: 0.5, color: "#666" }}>
            {suffix}
          </Typography>
        )}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: "#999", display: "block", mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function Card({ title, caption, children }) {
  return (
    <Box sx={{
      p: 3,
      borderRadius: 3,
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 1 }}>
          {caption}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" sx={{ color: "#5b6e00", fontWeight: 700, letterSpacing: 1 }}>
      {children}
    </Typography>
  );
}

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

const DoggoDigMetrics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    AxiosInstance.get(`${ApiList.DIG_METRICS}?days=${days}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.s === 1) {
          setData(res.data.r);
        } else {
          setError(res?.data?.m || "Failed to load dig metrics");
        }
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [days]);

  const s = data?.summary || {};
  const freq = data?.frequency || {};
  const doggo = data?.doggo || {};

  const dailyChart = (data?.daily || []).map((d) => ({
    date: fmtDate(d.date),
    Digs: Number(d.digs),
    Diggers: Number(d.diggers),
    "TP paid": Number(d.tp_paid),
  }));

  const haulChart = (data?.haul_mix || []).map((h) => ({
    quality: HAUL_LABELS[h.haul_quality] || h.haul_quality,
    Digs: Number(h.digs),
  }));

  return (
    <Box sx={{ p: 3, backgroundColor: "#fafafa", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Doggo &amp; Daily Dig</Typography>
        <Select
          size="small"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          sx={{ minWidth: 200, backgroundColor: "#fff" }}
        >
          {RANGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>
      )}
      {error && <Typography sx={{ color: "error.main", p: 2 }}>{error}</Typography>}

      {!loading && !error && data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Adoption */}
          <Box>
            <SectionLabel>Daily Dig · adoption</SectionLabel>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
              <StatCard label="Total digs" value={s.total_digs} />
              <StatCard label="Unique diggers" value={s.unique_diggers} />
              <StatCard
                label="Penetration"
                value={s.penetration_pct}
                suffix="%"
                hint={`of ${s.active_walkers} users who walked in this window`}
              />
              <StatCard
                label="Reveal completion"
                value={s.reveal_pct}
                suffix="%"
                hint="digs fully scratched open"
              />
            </Box>
          </Box>

          {/* Habit */}
          <Box>
            <SectionLabel>Daily Dig · habit shape</SectionLabel>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
              <StatCard label="Dug once" value={freq.one_time} hint="tried it, never came back" />
              <StatCard label="Casual (2–4 digs)" value={freq.casual} />
              <StatCard label="Habitual (5+ digs)" value={freq.habitual} />
              <StatCard label="Avg streak at dig time" value={s.avg_streak} hint="skewed by long-streak power users" />
            </Box>
          </Box>

          {/* Economy */}
          <Box>
            <SectionLabel>Daily Dig · economy</SectionLabel>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
              <StatCard label="TP paid out" value={s.tp_paid} />
              <StatCard label="Avg TP / dig" value={s.avg_tp_per_dig} hint="sim envelope: ~15 (S0) → ~50 (S9)" />
              <StatCard label="Item drops" value={s.item_drops} />
              <StatCard label="Jackpots" value={s.jackpots} />
            </Box>
          </Box>

          {/* Pick a pile (2026-09-09): the four numbers Victor asked to watch */}
          {data.pick_a_pile && (
            <Box>
              <SectionLabel>Daily Dig · pick a pile</SectionLabel>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
                <StatCard label="Opened" value={data.pick_a_pile.opened} hint="digs opened in pick mode" />
                <StatCard
                  label="Completion"
                  value={data.pick_a_pile.completion_pct}
                  suffix="%"
                  hint={`${data.pick_a_pile.completed} picked a pile`}
                />
                <StatCard
                  label="Next-day return"
                  value={data.pick_a_pile.next_day_return_pct}
                  suffix="%"
                  hint={`of ${data.pick_a_pile.retention_sample} digs (yesterday and earlier)`}
                />
                <StatCard
                  label="Return after near-miss"
                  value={data.pick_a_pile.near_miss_return_pct}
                  suffix="%"
                  hint={`vs ${data.pick_a_pile.best_pick_return_pct}% after picking the best pile`}
                />
                <StatCard
                  label="Near misses"
                  value={data.pick_a_pile.near_miss_pct}
                  suffix="%"
                  hint="picked less than the best pile"
                />
                <StatCard
                  label="Pile position"
                  value={`${data.pick_a_pile.pick_left || 0} / ${data.pick_a_pile.pick_middle || 0} / ${data.pick_a_pile.pick_right || 0}`}
                  hint="left / middle / right — should stay roughly even"
                />
                <StatCard
                  label="Avg picked vs best TP"
                  value={`${data.pick_a_pile.avg_picked_tp ?? 0} / ${data.pick_a_pile.avg_best_tp ?? 0}`}
                />
              </Box>
            </Box>
          )}

          <Card title="Digs per day">
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={dailyChart} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Digs" stroke="#b9cc03" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Diggers" stroke="#3f88c5" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Card>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <Card title="Haul quality mix">
              <Box sx={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={haulChart} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="quality" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="Digs" fill="#b9cc03" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Card>

            <Card
              title="TreatPoint flows"
              caption="Net TP by transaction type in this window (negative = spent by users)"
            >
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 90px 70px 100px", rowGap: 1.2, columnGap: 2, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Txns</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Users</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Net TP</Typography>
                {(data.economy || []).map((row) => (
                  <React.Fragment key={row.transaction_type}>
                    <Typography variant="body2">
                      {ECONOMY_LABELS[row.transaction_type] || row.transaction_type}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>{row.transactions}</Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>{row.users}</Typography>
                    <Typography variant="body2" sx={{
                      textAlign: "right",
                      fontWeight: 600,
                      color: Number(row.tp_net) < 0 ? "#c0392b" : "#5b6e00",
                    }}>
                      {row.tp_net}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Card>
          </Box>

          {/* Doggo engagement */}
          <Box>
            <SectionLabel>Doggo · customization &amp; shop</SectionLabel>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
              <StatCard label="Users with a Doggo loadout" value={doggo.loadout_users} hint="all time" />
              <StatCard label="Named their Doggo" value={doggo.named_doggos} hint="all time" />
              <StatCard label="Loadouts touched in window" value={doggo.loadouts_updated} />
              <StatCard label="Item owners" value={doggo.item_owners} hint="all time" />
              <StatCard label="Items owned" value={doggo.items_owned} hint="all time" />
            </Box>
          </Box>

          <Card title="Top diggers" caption="Up to 25, ranked by digs in this window">
            <Box sx={{ display: "grid", gridTemplateColumns: "60px 1.4fr 1fr 70px 90px 90px 70px 100px", rowGap: 1.2, columnGap: 2, mt: 1, overflowX: "auto" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>ID</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Name</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Username</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Digs</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Max streak</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>TP earned</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Items</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Last dig</Typography>
              {(data.top_diggers || []).map((row) => (
                <React.Fragment key={row.user_id}>
                  <Typography variant="body2">{row.user_id}</Typography>
                  <Typography variant="body2">{(row.name || "").trim() || "—"}</Typography>
                  <Typography variant="body2">{row.username || "—"}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right", fontWeight: 600 }}>{row.digs}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>{row.max_streak}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>{row.tp_earned}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>{row.item_drops}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right" }}>{fmtDate(row.last_dig)}</Typography>
                </React.Fragment>
              ))}
            </Box>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default DoggoDigMetrics;
